import { useMemo } from 'react';
import { useStudentViewers } from '../../api/hooks';
import { Badge, Initials, PagedFooter, Term } from '../../components/ui';
import { usePagination } from '../../lib/usePagination';
import { nameOr } from '../../lib/names';
import { prettifySlug } from '../../lib/caseStatus';

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.floor((now - then) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Artboard "Recent viewers": newest first, each row an initials tile, the name, "role · tab" and when. */
export default function RecentViewersPanel({ studentId }: { studentId: number }) {
  const { data, isLoading, isError } = useStudentViewers(studentId);
  const viewers = useMemo(
    () => [...(data?.viewers ?? [])].sort((a, b) => new Date(b.viewed_at).getTime() - new Date(a.viewed_at).getTime()),
    [data?.viewers],
  );
  // The log grows with every visit: a few at a time keeps the rail short.
  const pg = usePagination(viewers, 5, studentId);

  return (
    <section className="min-w-0 rounded-[20px] border border-line bg-surface p-[22px] shadow-card">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink"><Term k="recent_viewers">Recent viewers</Term></h2>
        <Badge tone="neutral" size="sm">Admin only</Badge>
      </div>
      {isLoading && <p className="text-xs text-muted">Loading…</p>}
      {isError && <p className="text-xs text-danger">Failed to load.</p>}
      {data && viewers.length === 0 && (
        <p className="text-xs text-muted">No views recorded yet.</p>
      )}
      {viewers.length > 0 && (
        <ul>
          {pg.pageItems.map((v, idx) => {
            const name = nameOr(v.viewer_name, v.viewer_username);
            return (
              <li key={`${v.viewer_username}-${v.viewed_at}-${idx}`} className="flex items-center gap-3 border-b border-hairline py-[11px] last:border-b-0">
                <Initials name={name} />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-ink">{name}</span>
                  <span className="text-xs text-muted">{[v.viewer_role && prettifySlug(v.viewer_role), v.tab].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="whitespace-nowrap text-xs text-muted">{relativeTime(v.viewed_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
      {pg.pageCount > 1 && <PagedFooter className="mt-2" label="Recent viewers pagination" {...pg} onPage={pg.setPage} />}
    </section>
  );
}
